import React from "react";
import { tenure, KINDS, fmtDate } from "@/lib/seniority";

/**
 * Vechimile angajatului (în muncă, în specialitate, la angajator): dată de început + „calculează automat” (implicit) sau
 * valoare manuală (ani, luni). Se aplică angajatului, deci tuturor rapoartelor lui.
 * `value` = {work|specialty|employer: {start, auto, years, months, touched?}}; `info` = răspunsul /mdm/employments/seniority
 * (reference_date, default_employer_start). La angajator, o dată necompletată urmează data primului CIM.
 */
export default function SeniorityFields({ value, onChange, info, readOnly }) {
  if (!value || !info) return <div style={{ gridColumn: "1/-1", fontSize: 12, color: "#64748B" }}>Se încarcă vechimile…</div>;
  const set = (kind, patch) => onChange({ ...value, [kind]: { ...value[kind], ...patch } });
  return (
    <>
      <div style={{ gridColumn: "1/-1", fontSize: 12, fontWeight: 700, color: "#334155", marginTop: 6 }}>Vechime</div>
      <div style={{ gridColumn: "1/-1", fontSize: 11, color: "#64748B", marginTop: -6 }}>
        Se aplică angajatului, la toate rapoartele lui. Calculul merge până la {fmtDate(info.reference_date)} (ultima zi lucrătoare a lunii).
      </div>
      {KINDS.map(([kind, label]) => {
        const v = value[kind];
        const implicitEmployer = kind === "employer" && !v.start && !v.touched;
        const shownStart = v.start || (implicitEmployer ? info.default_employer_start || "" : "");
        const calc = tenure(shownStart, info.reference_date);
        return (
          <div key={kind} style={{ gridColumn: "1/-1", display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(240px,1fr))", gap: 12 }}>
            <div>
              <label className="label">Dată început {label.toLowerCase()}</label>
              <input className="input" type="date" value={shownStart} disabled={readOnly}
                onChange={e => set(kind, { start: e.target.value, touched: true })} data-testid={`sen-${kind}-start`} />
              {implicitEmployer && info.default_employer_start && (
                <div style={{ fontSize: 11, color: "#64748B" }}>Completat automat: data primului CIM.</div>)}
            </div>
            <div>
              <label className="label">{label} (ani, luni)</label>
              {v.auto ? (
                <div className="input" style={{ background: "#F1F5F9", display: "flex", alignItems: "center" }} data-testid={`sen-${kind}-calc`}>
                  {calc ? `${calc.years} ani, ${calc.months} luni` : "—"}
                </div>
              ) : (
                <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                  <input className="input" type="number" min="0" max="80" value={v.years} disabled={readOnly}
                    onChange={e => set(kind, { years: e.target.value })} data-testid={`sen-${kind}-years`} /> ani
                  <input className="input" type="number" min="0" max="11" value={v.months} disabled={readOnly}
                    onChange={e => set(kind, { months: e.target.value })} data-testid={`sen-${kind}-months`} /> luni
                </div>)}
              <label style={{ display: "flex", gap: 6, alignItems: "center", fontSize: 12, marginTop: 4 }}>
                <input type="checkbox" checked={!!v.auto} disabled={readOnly} onChange={e => set(kind, { auto: e.target.checked })} data-testid={`sen-${kind}-auto`} />
                Calculează automat
              </label>
            </div>
          </div>
        );
      })}
      {value.education && (() => {          // vechimea în învățământ — doar cu modulul Stat de Funcții Didactic licențiat
        const v = value.education;
        const calc = tenure(v.start, info.reference_date);
        return (
          <div style={{ gridColumn: "1/-1", display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(240px,1fr))", gap: 12 }}>
            <div>
              <label className="label">Dată început vechime în învățământ</label>
              <input className="input" type="date" value={v.start || ""} disabled={readOnly}
                onChange={e => set("education", { start: e.target.value })} data-testid="sen-education-start" />
            </div>
            <div>
              <label className="label">Vechime în învățământ (ani, luni)</label>
              <div className="input" style={{ background: "#F1F5F9", display: "flex", alignItems: "center" }} data-testid="sen-education-calc">
                {calc ? `${calc.years} ani, ${calc.months} luni` : "—"}
              </div>
            </div>
          </div>);
      })()}
    </>
  );
}

/** Starea formularului din răspunsul serverului. */
export function seniorityFormState(info) {
  const o = {};
  for (const [kind] of KINDS) {
    const r = info[kind];
    o[kind] = { start: r.start || "", auto: r.auto, years: r.manual_years, months: r.manual_months, touched: !!r.start };
  }
  if (info.education) o.education = { start: info.education.start || "" };     // doar cu SFD licențiat
  return o;
}

/** Corpul trimis la salvare: la angajator, o dată neatinsă rămâne goală (urmează primul CIM). */
export function seniorityPayload(state, withEducation = false) {
  const out = {};
  if (withEducation && state.education) out.education = { start: state.education.start || null };
  for (const [kind] of KINDS) {
    const v = state[kind];
    out[kind] = { start: v.start || null, auto: !!v.auto, years: parseInt(v.years, 10) || 0, months: parseInt(v.months, 10) || 0 };
  }
  return out;
}
